import { memo, useContext } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";
import { ScreenReaderText, ThemeWrapper } from "@verity/components";
import { PortalControllerContext } from "../../context";
import { useGetRosterStatisticsQuery } from "./store/roster-api";
import { RosterStatisticCard } from "./roster-statistic-card";
import { AdminsIcon, InstructorsIcon, StudentsIcon, UsersIcon } from "./roster-icons";
import { MemoizedRosterTable } from "./roster-table";
import VisualizerBanner from "../visualizer-banner";
import styles from "./roster-page.module.css";

const RosterPage = () => {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteUuid } = portalContext;
	const { data: statistics, isLoading } = useGetRosterStatisticsQuery(instituteUuid ?? skipToken);

	const { t } = useTranslation();

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.wrapper }}>
			<div className={styles.containerBackground}>
				<ScreenReaderText component="h1">{t("portal.components.roster.title")}</ScreenReaderText>
				<div className={styles.container}>
					<VisualizerBanner />
					<div className={styles.headerContainer}>
						<RosterStatisticCard
							Icon={UsersIcon}
							title={t("portal.components.roster.statistics.totalUsers")}
							count={
								isLoading
									? undefined
									: (statistics?.admin ?? 0) + (statistics?.instructor ?? 0) + (statistics?.student ?? 0)
							}
							color="#42296E"
						/>
						<RosterStatisticCard
							Icon={AdminsIcon}
							title={t("portal.components.roster.statistics.totalAdmins")}
							count={isLoading ? undefined : statistics?.admin}
							color="#C728FF"
						/>
						<RosterStatisticCard
							Icon={InstructorsIcon}
							title={t("portal.components.roster.statistics.totalInstructors")}
							count={isLoading ? undefined : statistics?.instructor}
							color="#7064B7"
						/>
						{(lms || (statistics?.student != null && statistics.student > 0)) && (
							<RosterStatisticCard
								Icon={StudentsIcon}
								title={t("portal.components.roster.statistics.totalStudents")}
								count={isLoading ? undefined : statistics?.student}
								color="#1F6DD4"
							/>
						)}
					</div>
					<MemoizedRosterTable />
				</div>
			</div>
		</ThemeWrapper>
	);
};

export const MemoizedRosterPage = memo(RosterPage);
